import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useEvents } from '../../hooks/useEvents'
import { parseIsoDate } from '../../lib/dates'
import { FilterPill } from '../FilterPill'
import type { EventListItem, EventStatus } from '../../types'

// Events overview — Calendar tab 2 (spec 40). Multi-month mini-grid of the
// year's real events, coloured by event type. Read-only; click a day to expand
// its events, click an event to open its plan. Extracted verbatim from the old
// Calendar page — the year now comes from the shared Calendar shell (spec 41).
//
// Each event appears on every day it spans (start..end inclusive). Filters
// (type/status/hide-past) compose and are local to this tab.

const WEEKDAY_HEAD = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const STATUS_OPTIONS: ReadonlyArray<EventStatus | 'all'> = [
  'all', 'Draft', 'In Development', 'Live', 'Complete', 'Cancelled'
]

export function EventsOverviewTab({ year }: { year: number }) {
  const { data: events, isLoading, error } = useEvents()
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all')
  const [hidePastMonths, setHidePastMonths] = useState<boolean>(true)
  const [openDay, setOpenDay] = useState<string | null>(null)

  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentMonthIdx = now.getUTCMonth()

  const availableTypes = useMemo(() => {
    if (!events) return [] as string[]
    const set = new Set<string>()
    const yearStart = new Date(Date.UTC(year, 0, 1)).getTime()
    const yearEnd = new Date(Date.UTC(year + 1, 0, 1)).getTime()
    for (const e of events) {
      const s = parseIsoDate(e.startDate).getTime()
      const eEnd = parseIsoDate(e.endDate).getTime()
      if (eEnd >= yearStart && s < yearEnd) set.add(e.type.name)
    }
    return Array.from(set).sort()
  }, [events, year])

  const visibleEvents = useMemo(() => {
    if (!events) return [] as EventListItem[]
    return events.filter((e) => {
      if (e.status === 'Idea') return false
      if (typeFilter !== 'all' && e.type.name !== typeFilter) return false
      if (statusFilter !== 'all' && e.status !== statusFilter) return false
      return true
    })
  }, [events, typeFilter, statusFilter])

  const byDate = useMemo(() => {
    const m = new Map<string, EventListItem[]>()
    for (const e of visibleEvents) {
      const start = parseIsoDate(e.startDate)
      const end = parseIsoDate(e.endDate)
      const cur = new Date(start.getTime())
      while (cur.getTime() <= end.getTime()) {
        const key = isoDate(cur)
        const arr = m.get(key) ?? []
        arr.push(e)
        m.set(key, arr)
        cur.setUTCDate(cur.getUTCDate() + 1)
      }
    }
    return m
  }, [visibleEvents])

  const filterActive = typeFilter !== 'all' || statusFilter !== 'all'

  const visibleMonths = useMemo<number[]>(() => {
    const all = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
    if (!hidePastMonths) return all
    if (year > currentYear) return all
    if (year < currentYear) return []
    return all.filter((m) => m >= currentMonthIdx)
  }, [hidePastMonths, year, currentYear, currentMonthIdx])

  return (
    <div className="space-y-5">
      <p className="text-ink-soft">Every event in the {year} programme. Click a day to dive in.</p>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-line rounded px-4 py-2.5">
        <FilterPill
          label="Hide Past Months"
          active={hidePastMonths}
          onClick={() => setHidePastMonths((v) => !v)}
        />
        <FilterSelect
          label="Type"
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { value: 'all', label: 'All types' },
            ...availableTypes.map((t) => ({ value: t, label: t }))
          ]}
        />
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as EventStatus | 'all')}
          options={STATUS_OPTIONS.map((s) => ({
            value: s,
            label: s === 'all' ? 'All statuses' : s
          }))}
        />
        <div className="flex-1" />
        <div className="text-xs text-ink-soft font-mono">
          {visibleEvents.length} of {events?.length ?? 0} events
          {filterActive && (
            <button
              type="button"
              onClick={() => { setTypeFilter('all'); setStatusFilter('all') }}
              className="ml-3 text-green hover:underline"
            >
              clear filters
            </button>
          )}
        </div>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading…</div>}
      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
          Could not load events: {(error as Error).message}
        </div>
      )}

      {events && visibleMonths.length > 0 && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {visibleMonths.map((m) => (
            <MonthCard
              key={m}
              year={year}
              month={m}
              byDate={byDate}
              openDay={openDay}
              onDayClick={(iso) => setOpenDay(openDay === iso ? null : iso)}
            />
          ))}
        </div>
      )}

      {events && visibleMonths.length === 0 && (
        <div className="bg-white border border-line rounded p-8 text-center text-sm text-ink-soft">
          Every month in {year} is in the past. Toggle off "Hide Past Months" to see them.
        </div>
      )}

      {events && visibleMonths.length > 0 && visibleEvents.length === 0 && filterActive && (
        <div className="bg-white border border-line rounded p-8 text-center text-sm text-ink-soft">
          No events match the current filters.
        </div>
      )}
    </div>
  )
}

// ---- Filter select -------------------------------------------------------

function FilterSelect({
  label, value, onChange, options
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
}) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-semibold uppercase tracking-wider text-ink-soft">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-line rounded px-2 py-1 text-sm bg-white focus:outline-none focus:border-green"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

// ---- Month card ----------------------------------------------------------

function MonthCard({
  year, month, byDate, openDay, onDayClick
}: {
  year: number
  month: number
  byDate: Map<string, EventListItem[]>
  openDay: string | null
  onDayClick: (iso: string) => void
}) {
  const firstUtc = new Date(Date.UTC(year, month, 1))
  const offset = (firstUtc.getUTCDay() + 6) % 7
  const lastUtc = new Date(Date.UTC(year, month + 1, 0))
  const dayCount = lastUtc.getUTCDate()

  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`
  const openMatches = openDay && openDay.startsWith(monthPrefix)
    ? byDate.get(openDay) ?? []
    : []

  const monthEvents = useMonthEventsList(year, month, byDate)

  return (
    <div className="bg-white border border-line rounded p-3.5">
      <div className="flex items-baseline justify-between mb-2 px-1">
        <div className="font-display text-base font-semibold">{MONTH_NAMES[month]}</div>
        {monthEvents.length > 0 && (
          <div className="text-[10px] text-ink-soft font-mono uppercase tracking-wide">
            {monthEvents.length} event{monthEvents.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
      <div className="grid grid-cols-7 text-center text-[9px] uppercase tracking-wide text-ink-soft mb-1.5">
        {WEEKDAY_HEAD.map((w, i) => <div key={i}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: offset }).map((_, i) => <div key={`pad-${i}`} />)}
        {Array.from({ length: dayCount }).map((_, i) => {
          const day = i + 1
          const iso = isoDate(new Date(Date.UTC(year, month, day)))
          const dayEvents = byDate.get(iso)
          const isOpen = openDay === iso
          return (
            <DayCell
              key={day}
              day={day}
              events={dayEvents}
              isOpen={isOpen}
              onClick={() => dayEvents && dayEvents.length > 0 && onDayClick(iso)}
            />
          )
        })}
      </div>
      {openMatches.length > 0 && (
        <div className="mt-2 pt-2 border-t border-line space-y-1">
          {openMatches.map((e) => {
            const isArchived = e.archivedAt != null
            return (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                className={
                  'flex items-center gap-2 px-1.5 py-1 rounded text-xs hover:bg-parchment ' +
                  (isArchived ? 'opacity-60 italic' : '')
                }
              >
                <span
                  className="inline-block w-1 h-3.5 rounded-full flex-shrink-0"
                  style={{ background: e.type.colourBorder }}
                />
                <span className={'font-semibold truncate flex-1 ' + (isArchived ? 'line-through' : '')}>
                  {e.name}
                </span>
                {isArchived && (
                  <span
                    aria-label="Archived"
                    className="text-[9px] px-1 py-0.5 rounded font-semibold uppercase tracking-wide flex-shrink-0 bg-ink-soft/15 text-ink-soft"
                  >
                    Archived
                  </span>
                )}
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide flex-shrink-0"
                  style={{ background: e.type.colourBg, color: e.type.colourText }}
                >
                  {e.type.name}
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DayCell({
  day, events, isOpen, onClick
}: {
  day: number
  events: EventListItem[] | undefined
  isOpen: boolean
  onClick: () => void
}) {
  if (!events || events.length === 0) {
    return (
      <div className="aspect-square text-xs rounded flex items-center justify-center text-ink-soft/60">
        {day}
      </div>
    )
  }
  if (isOpen) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="aspect-square text-xs rounded bg-green text-parchment font-semibold flex items-center justify-center"
      >
        {day}
      </button>
    )
  }
  const primaryColour = events[0]!.type.colourBorder
  return (
    <button
      type="button"
      onClick={onClick}
      className="aspect-square text-xs rounded relative flex items-center justify-center hover:bg-parchment cursor-pointer overflow-hidden"
      title={events.map((e) => e.name).join(', ')}
    >
      <span className="relative z-10">{day}</span>
      <span
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ background: primaryColour }}
      />
      {events.length > 1 && (
        <span
          aria-hidden
          className="absolute top-0.5 right-0.5 text-[8px] font-mono font-bold px-0.5 rounded leading-none"
          style={{ background: primaryColour, color: '#fff' }}
        >
          {events.length}
        </span>
      )}
    </button>
  )
}

function useMonthEventsList(
  year: number,
  month: number,
  byDate: Map<string, EventListItem[]>
): EventListItem[] {
  return useMemo(() => {
    const seen = new Set<number>()
    const list: EventListItem[] = []
    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`
    for (const [iso, evts] of byDate) {
      if (!iso.startsWith(monthPrefix)) continue
      for (const e of evts) {
        if (!seen.has(e.id)) {
          seen.add(e.id)
          list.push(e)
        }
      }
    }
    return list.sort((a, b) => a.startDate.localeCompare(b.startDate))
  }, [year, month, byDate])
}

// ---- Helpers -------------------------------------------------------------

function isoDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}
